import type { InvoiceCandidateBillableDateSource, InvoiceCandidateOrderRecord } from '@navio/nsdk';
import type { ColumnDef } from '@tanstack/react-table';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { NoddiBadge, NoddiLink } from 'noddi-ui-web';
import { formatCurrencyAmount, NoddiDateFormats, noddiFormatDate } from 'noddi-util';

import { BILLABLE_DATE_SOURCE_LABELS, EXCLUSION_REASON_LABELS, ORDER_TYPE_OPTIONS } from './invoiceCandidateLabels';
import { InvoiceCandidateStatusLegend } from './InvoiceCandidateStatusLegend';

const ORDER_TYPE_BY_VALUE = new Map(ORDER_TYPE_OPTIONS.map((option) => [option.value as string, option]));

/**
 * Which event dated the row, in the icon that says why it is trustworthy.
 *
 * `order_created` is the fallback: nothing recorded the work being finished, so
 * the date says when the order was raised and nothing about when it was served.
 */
const DATE_SOURCE_ICONS: Record<InvoiceCandidateBillableDateSource, NoddiIconName> = {
  booking_completed: 'CircleCheck',
  quote_fulfilled: 'ShoppingCart',
  order_created: 'Plus'
};

type Options = {
  /**
   * Name the department and organization behind each row.
   *
   * Off where the host is already scoped to one seller -- partner staff work
   * inside a single department, so the column repeats the same two names down
   * the whole table.
   */
  showDepartment: boolean;
  /** Hosts with a booking page pass the path. A shop row has no booking. */
  bookingTo?: (bookingId: number) => string;
};

export const getInvoiceCandidateColumns = ({
  showDepartment,
  bookingTo
}: Options): ColumnDef<InvoiceCandidateOrderRecord>[] => [
  {
    accessorKey: 'orderSlug',
    header: 'Order',
    cell: ({ row }) => (
      <div className='flex flex-col gap-0.5'>
        <span className='font-medium'>{row.original.orderSlug}</span>
        <span className='text-xs text-muted-foreground'>#{row.original.orderId}</span>
      </div>
    )
  },
  {
    accessorKey: 'orderType',
    header: 'Type',
    cell: ({ row }) => {
      const orderType = ORDER_TYPE_BY_VALUE.get(row.original.orderType);
      return (
        <NoddiBadge variant='outline'>
          {orderType && <NoddiIcon name={orderType.icon} size='xSmall' />}
          {orderType?.rowLabel ?? row.original.orderType}
        </NoddiBadge>
      );
    }
  },
  {
    accessorKey: 'bookingSlug',
    header: 'Booking',
    cell: ({ row }) => {
      const booking = row.original.booking;
      if (booking == null) {
        return null;
      }
      const to = bookingTo?.(booking.id);
      if (to == null) {
        return booking.slug;
      }
      return (
        <NoddiLink
          variant='link'
          className='h-auto p-0 font-normal whitespace-normal no-underline'
          spanClassName='underline underline-offset-2'
          to={to}
        >
          {booking.slug}
        </NoddiLink>
      );
    }
  },
  {
    accessorKey: 'billableDate',
    header: 'Billable date',
    minSize: 150,
    meta: {
      description:
        'The date the order counts against for the period, and the event it came from: a booking completing, a shop purchase being fulfilled, or -- when neither happened -- the order being raised.'
    },
    cell: ({ row }) => (
      <div className='flex flex-col gap-0.5 whitespace-nowrap'>
        <span>{noddiFormatDate(new Date(row.original.billableDate), NoddiDateFormats.DATE)}</span>
        <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
          <NoddiIcon name={DATE_SOURCE_ICONS[row.original.billableDateSource]} size='xSmall' className='shrink-0' />
          {BILLABLE_DATE_SOURCE_LABELS[row.original.billableDateSource]}
        </span>
      </div>
    )
  },
  ...(showDepartment
    ? ([
        {
          accessorKey: 'serviceDepartmentName',
          header: 'Department',
          meta: {
            description:
              'Who performed and sells the work. An invoice carries one organization, so rows have to agree on it.'
          },
          cell: ({ row }) => (
            <div className='flex min-w-0 flex-col gap-0.5'>
              <span className='truncate'>{row.original.serviceDepartmentName}</span>
              <span className='truncate text-xs text-muted-foreground'>{row.original.serviceOrganizationName}</span>
            </div>
          )
        }
      ] satisfies ColumnDef<InvoiceCandidateOrderRecord>[])
    : []),
  {
    accessorKey: 'billableAmount',
    header: 'Amount',
    cell: ({ row }) => formatCurrencyAmount({ money: row.original.billableAmount })
  },
  {
    accessorKey: 'exclusionReason',
    header: 'Status',
    meta: {
      // A legend rather than a paragraph: the same badges the column renders,
      // each against what it means, so the reader matches on color and shape
      // instead of finding the right clause in a wall of text.
      descriptionClassName: 'max-w-80',
      description: <InvoiceCandidateStatusLegend />
    },
    cell: ({ row }) =>
      row.original.exclusionReason ? (
        <NoddiBadge variant='destructive'>{EXCLUSION_REASON_LABELS[row.original.exclusionReason]}</NoddiBadge>
      ) : (
        <NoddiBadge variant='success'>Will be billed</NoddiBadge>
      )
  }
];
